/**
 * What every ADR-0010 component shares: a client built from the host's three
 * props, the host's answer to "what may I do here", and a small loader.
 *
 * Nothing in this package imports a host's session, org or config, the shell or
 * the router. The host — `apps/web`'s route files, or a sibling app's — supplies
 * the fetcher, the org and the service URL, and renders the result inside its
 * own `<PlatformShell>`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  ApiError,
  DOCUMENT,
  actedForLabel,
  createProDocClient,
  pairRefusalOf,
  type AccessAnswer,
  type DocumentVersion,
  type Fetcher,
  type PairRefusal,
  type ProDocClient,
  type Verb,
} from './prodoc-client'

/** The three props every component takes (ADR-0010 §Decision), and one courtesy. */
export interface HostProps {
  /** Where `prodoc-service` answers, e.g. `http://localhost:8170`. */
  apiUrl: string
  /** The host's authenticated fetcher — its own session store's `authed`. */
  fetcher: Fetcher
  /** The host's selected organisation. */
  orgId: string
  /**
   * The signed-in person, so a screen can say "you" for their own uploads.
   * The envelope carries the uploader's id and no name; the organisation's
   * member directory is the console's, not ours.
   */
  userId?: string | null
}

/** "you" for the host's own user; otherwise a member, unnamed. */
export function uploaderLabel(uploadedBy: string, userId: string | null | undefined): string {
  return userId && uploadedBy === userId ? 'you' : 'another member'
}

/**
 * Who stored a version, for its line: "you" or "another member" for a person
 * here; for an app, the app and whom it acted for (FR-40) — "ProDesk for you",
 * "ProDesk for another member", "ProDesk, for a client contact", "ProDesk".
 */
export function versionUploaderLabel(
  version: Pick<DocumentVersion, 'actor_kind' | 'actor_service' | 'uploaded_by' | 'uploaded_party'>,
  userId: string | null | undefined,
): string {
  if (version.actor_kind === 'app') {
    return actedForLabel({ service: version.actor_service, user_id: version.uploaded_by, party: version.uploaded_party ?? null }, userId)
  }
  return uploaderLabel(version.uploaded_by ?? '', userId)
}

export function useProDocClient({ apiUrl, fetcher, orgId }: HostProps): ProDocClient {
  // A new fetcher identity on every render would rebuild the client and refetch
  // everything; hosts pass a stable store method, so this memo holds.
  return useMemo(() => createProDocClient({ baseUrl: apiUrl, fetcher, orgId }), [apiUrl, fetcher, orgId])
}

export interface Loaded<T> {
  data: T | null
  loading: boolean
  error: string
  reload: () => Promise<void>
  /** Replace what is shown without a round trip — after a mutation returned the fresh row. */
  set: (next: T) => void
}

/** Load once per `deps`; a slower answer to an older question never overwrites a newer one. */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[]): Loaded<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const latest = useRef(0)
  const reload = useCallback(async () => {
    const ticket = ++latest.current
    try {
      const next = await load()
      if (ticket !== latest.current) return
      setData(next)
      setError('')
    } catch (e) {
      if (ticket !== latest.current) return
      setError(errorText(e))
    } finally {
      if (ticket === latest.current) setLoading(false)
    }
    // `deps` is the caller's statement of when the question changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  useEffect(() => {
    setLoading(true)
    void reload()
  }, [reload])
  return { data, loading, error, reload, set: setData }
}

/** Where typing happens: a key pressed in one is the person's text, never a command. */
const TYPING = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
/** An open dialog anywhere on the page — the kit's renders only while open, so being there is being open. */
const OPEN_DIALOG = '[role=dialog], [role=alertdialog]'

/**
 * W24 (FR-56, D73): which way a key press walks a review queue — `J` the next,
 * `K` the previous — or `null`. **Never** a key typed into an input, a text area,
 * a select or editable content; never one with a modifier held (Ctrl, ⌘, Alt or
 * Shift — those are the screen's own shortcuts, or the browser's); never a key a
 * handler already took, one still being composed, or one pressed while a dialog
 * is open on the page.
 */
export function queueKeyOf(e: KeyboardEvent): 'next' | 'prev' | null {
  if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return null
  const key = e.key.toLowerCase()
  if (key !== 'j' && key !== 'k') return null
  const target = e.target instanceof Element ? e.target : null
  if (target && (target.closest(TYPING) || (target instanceof HTMLElement && target.isContentEditable))) return null
  if (typeof document !== 'undefined' && document.querySelector(OPEN_DIALOG)) return null
  return key === 'j' ? 'next' : 'prev'
}

/**
 * `J` / `K` walk a review queue (FR-56, D73): `next` and `prev` are what they do,
 * either left out where there is nowhere to go. Registered once on the window;
 * the latest handlers are read at the key press. Off while `enabled` is false — a
 * dialog of the screen's own, say.
 */
export function useQueueKeys({ next, prev, enabled = true }: { next?: (() => void) | null; prev?: (() => void) | null; enabled?: boolean }): void {
  const latest = useRef({ next, prev, enabled })
  latest.current = { next, prev, enabled }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const h = latest.current
      if (!h.enabled) return
      const way = queueKeyOf(e)
      const go = way === 'next' ? h.next : way === 'prev' ? h.prev : null
      if (!go) return
      e.preventDefault()
      go()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])
}

/** What a sibling's refused pair (FR-33) says: not "not found", not the paywall — a connection, and why it is off. */
export const PAIR_REFUSAL_TITLE = 'ProDoc is not connected for this app in this organisation'

export function pairRefusalText(refusal: PairRefusal): { title: string; detail: string } {
  const app = refusal.app
  let detail: string
  switch (refusal.reason) {
    case 'not_enabled':
      detail = `An administrator of this organisation has to enable ProDoc for ${app} first.`
      break
    case 'entitlement_lapsed':
      detail = `The subscription behind the connection with ${app} has lapsed. Nothing already stored is affected.`
      break
    case 'authority_unreachable':
      detail = 'FiveOX could not confirm the connection just now. Try again in a moment.'
      break
    case 'scope_missing':
      detail = `The connection with ${app} does not allow ${refusal.scope === 'document.write' ? 'adding documents' : refusal.scope === 'document.read' ? 'reading documents' : (refusal.scope ?? 'this')}.`
      break
    default:
      detail = `FiveOX answered “${refusal.reason}” for the connection with ${app}.`
  }
  return { title: PAIR_REFUSAL_TITLE, detail: `${detail} (${refusal.integration})` }
}

/** The sentence a screen shows for a failure, whatever threw it. */
export function errorText(e: unknown, fallback = 'Something went wrong.'): string {
  const pair = pairRefusalOf(e)
  if (pair) {
    const { title, detail } = pairRefusalText(pair)
    return `${title}. ${detail}`
  }
  if (e instanceof ApiError) return e.message
  if (e instanceof Error && e.message) return e.message
  return fallback
}

/**
 * The organisation's workspaces, and a name for a workspace id. The envelope
 * carries `workspace_id` only, so every screen names it from this list; an id
 * the list does not hold (an archived workspace) reads as such.
 */
export function useWorkspaces(client: ProDocClient) {
  const workspaces = useLoad(() => client.workspaces.list(), [client])
  const names = useMemo(() => new Map((workspaces.data ?? []).map((w) => [w.id, w.name])), [workspaces.data])
  const nameOf = useCallback(
    (id: string | null | undefined): string | null => (id ? (names.get(id) ?? (workspaces.data ? 'An archived workspace' : null)) : null),
    [names, workspaces.data],
  )
  return { ...workspaces, list: workspaces.data ?? [], nameOf }
}

export interface Access {
  answer: AccessAnswer | null
  loading: boolean
  can: (resource: string, verb: Verb) => boolean
  /** Sight of every workspace (`can_view_all` on `document`). */
  seesAll: boolean
}

/**
 * `GET /api/v1/me/access` on `prodoc-service` — the same evaluation the API
 * enforces with (FR-10, FR-11). A component asks for itself rather than trusting
 * a prop, because a sibling host's own evaluation is for *its* app, not this one.
 * It fails closed: no answer, no verbs.
 */
export function useProDocAccess(client: ProDocClient): Access {
  const { data, loading } = useLoad(() => client.access(), [client])
  const can = useCallback(
    (resource: string, verb: Verb) => Boolean(data?.permissions[resource]?.[verb]),
    [data],
  )
  return { answer: data, loading, can, seesAll: can(DOCUMENT, 'can_view_all') }
}
